import classNames from 'classnames';
import { useId } from 'react';

import { Tariff, TariffCalculationResult } from 'models/extension-generated';

import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { formatPrice } from 'utils/string';
import { TariffDescription } from '../../TariffDescription';
import { Tooltip } from '@mui/material';

type TariffRowProps = {
  tariff: Tariff;
  calculation?: TariffCalculationResult;
  tariffsList: Array<Tariff>;
  isMobile: boolean;
};

export const TariffRow = ({ tariff, calculation, tariffsList, isMobile }: TariffRowProps) => {
  const tooltipId = useId();

  const getTariffDescription = (id: string) => {
    const rawDescription = tariffsList.find((tariff) => tariff.id === id)?.description;
    return <TariffDescription description={rawDescription!} />;
  };

  // Helper to get plain text for screen readers
  const getTariffDescriptionText = (id: string) => {
    const rawDescription = tariffsList.find((tariff) => tariff.id === id)?.description;
    if (!rawDescription) return '';
    return rawDescription.replace(/<[^>]*>/g, '');
  };

  function hasRiskPremium(calculation?: TariffCalculationResult): boolean {
    const risk = calculation?.calculationMonthly?.riskSurcharges;
    if (risk && risk > 0) {
      return true;
    }
    return false;
  }
  const tariffPrice = calculation?.calculationMonthly?.amount;

  // Create screen reader text in the required order: Tariff ID, risk surcharge, price, tooltip text
  const getScreenReaderText = () => {
    const parts = [];

    // 1. Tariff ID
    parts.push(`Gewählter Tarif: ${tariff.id}`);

    // 2. Risk surcharge (if present)
    if (hasRiskPremium(calculation)) {
      const riskAmount = formatPrice(calculation?.calculationMonthly?.riskSurcharges);
      parts.push(`Enthaltener Risikozuschlag: ${riskAmount}`);
    }

    // 3. Price
    if (tariffPrice) {
      parts.push(`Monatbeitrag in €: ${calculation?.calculationMonthly?.amount}`);
    } else {
      parts.push('Noch kein Tarifbeitrag ermittelt');
    }

    // 4. Tooltip text (tariff description)
    const rawDescription = tariffsList.find((t) => t.id === tariff.id)?.description;
    if (rawDescription) {
      // Remove HTML tags for screen reader
      const cleanDescription = rawDescription.replace(/<[^>]*>/g, '');
      parts.push(cleanDescription);
    }

    return parts.join('. ');
  };

  return (
    <div
      className="flex justify-between py-[4px] focus:outline-none focus:ring-2 focus:ring-primary-60 focus:ring-offset-2 rounded p-[4px] -m-[4px]"
      tabIndex={0}
      aria-label={getScreenReaderText()}
      role="button"
    >
      <Tooltip title={getTariffDescription(tariff.id)} arrow placement="left">
        <Chip
          tabIndex={0}
          label={tariff.id}
          className="rounded-[8px] text-white-100"
          variant="filled"
          color="primary"
          data-tariff-chip
          role="button"
          aria-describedby={tooltipId}
        />
      </Tooltip>
      <span id={tooltipId} className="sr-only">
        {getTariffDescriptionText(tariff.id)}
      </span>

      <div className="flex items-center" aria-hidden="true">
        {hasRiskPremium(calculation) && (
          <Typography variant="bodyMDRegular" className={classNames('pr-s mr-s text-[#9a9c9c]')}>
            {formatPrice(calculation?.calculationMonthly?.riskSurcharges)}
          </Typography>
        )}
        <Typography
          variant="bodyMDRegular"
          className={`m-[0px] ${isMobile ? 'text-white-100' : 'text-text-100'}`}
        >
          {tariffPrice && formatPrice(calculation?.calculationMonthly?.amount)}
          {!tariffPrice && '-,--'}
        </Typography>
      </div>
    </div>
  );
};
